/**
 * StepDone — final step of onboarding.
 * Shows four capability showcases, the terms-of-service checkbox, and the
 * "Go to dashboard" button that's gated by terms acceptance.
 */

import { BrandMark } from '../BrandLogo';

interface StepDoneProps {
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
  onGoToDashboard: () => void;
  onBack: () => void;
  isLoading: boolean;
}

interface ShowcaseCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

function ShowcaseCard({ title, description, icon }: ShowcaseCardProps) {
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-800/40 p-4 space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-primary-900/40 border border-primary-700/40 flex items-center justify-center text-primary-400">
          {icon}
        </div>
        <p className="font-semibold text-sm text-gray-100">{title}</p>
      </div>
      <p className="text-xs text-gray-400 leading-relaxed">{description}</p>
    </div>
  );
}

// Stroke icons — kept inline so we don't pull in an icon library.
const ChartIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M3 17l5-5 4 4 8-8M16 8h5v5" />
  </svg>
);

const SlidersIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4 7h6m4 0h6M4 12h12m4 0h0M4 17h4m4 0h12" />
    <circle cx="12" cy="7" r="1.6" fill="currentColor" />
    <circle cx="18" cy="12" r="1.6" fill="currentColor" />
    <circle cx="10" cy="17" r="1.6" fill="currentColor" />
  </svg>
);

const BotIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M9 19v-3m6 3v-3M5 11a4 4 0 014-4h6a4 4 0 014 4v3a4 4 0 01-4 4H9a4 4 0 01-4-4v-3zM9 12h.01M15 12h.01" />
  </svg>
);

const DashboardIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4 5h6v6H4V5zm0 8h6v6H4v-6zm10-8h6v10h-6V5zm0 12h6v2h-6v-2z" />
  </svg>
);

export function StepDone({
  accepted,
  onAcceptedChange,
  onGoToDashboard,
  onBack,
  isLoading,
}: StepDoneProps) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="relative shrink-0">
          <BrandMark className="w-12 h-12" />
          <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-500 border-2 border-gray-900 flex items-center justify-center">
            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <div>
          <h2 className="font-display font-extrabold text-2xl text-white leading-tight">
            You're all set.
          </h2>
          <p className="text-gray-400 text-sm mt-0.5">
            Here's what your agent can do once it's connected.
          </p>
        </div>
      </div>

      {/* Showcases — 2x2 grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ShowcaseCard
          icon={ChartIcon}
          title="Backtest any strategy"
          description="Single config or multi-asset aggregation — full metrics and equity curve in seconds."
        />
        <ShowcaseCard
          icon={SlidersIcon}
          title="Grid search & walk-forward"
          description="Try every parameter combo and validate the winners across rolling windows."
        />
        <ShowcaseCard
          icon={BotIcon}
          title="Deploy trading bots"
          description="Spin up paper sessions that run 24/7 — switch to live when ready."
        />
        <ShowcaseCard
          icon={DashboardIcon}
          title="Dashboard for every run"
          description="Equity curves, grid search heatmaps, trade logs, bot sessions — all here."
        />
      </div>

      {/* Terms acceptance */}
      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => onAcceptedChange(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border border-gray-600 bg-gray-800 accent-primary-500 cursor-pointer"
        />
        <span className="text-sm text-gray-300 leading-relaxed group-hover:text-gray-100 transition-colors">
          I have read and agree to the{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-400 hover:text-primary-300 underline underline-offset-2"
            onClick={(e) => e.stopPropagation()}
          >
            Terms of Service
          </a>
          .
        </span>
      </label>

      {/* Navigation */}
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={onBack}
          className="px-5 py-2.5 rounded-lg border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 transition-colors text-sm font-medium"
        >
          Back
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onGoToDashboard}
          disabled={!accepted || isLoading}
          className="btn-glow px-8 py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Saving...' : 'Go to dashboard'}
        </button>
      </div>
    </div>
  );
}
